import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Dimensions,
  TouchableOpacity,
} from "react-native";
import SignaturePad from "../../../../components/SignaturePad";
import SignatureDisclaimer from "../../../../components/SignatureDisclaimer";
import { useTheme } from "../../../../theme";

interface SignatureSectionProps {
  employeeSignatureFileName: string | null;
  employeeSignatureImageUrl: string | null;
  managerSignatureFileName: string | null;
  managerSignatureImageUrl: string | null;
  handleEmployeeSignature: (fileName: string | null, imageUrl?: string | null) => void;
  handleManagerSignature: (fileName: string | null, imageUrl?: string | null) => void;
  isViewOnlyMode?: boolean;
}

type SignatureType = "employee" | "manager";

const SignatureSection: React.FC<SignatureSectionProps> = ({
  employeeSignatureFileName,
  employeeSignatureImageUrl,
  managerSignatureFileName,
  managerSignatureImageUrl,
  handleEmployeeSignature,
  handleManagerSignature,
  isViewOnlyMode = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";

  // Check screen width for responsive layout
  const windowWidth = Dimensions.get("window").width;
  // Check if on iOS (iPad/iPhone) or smaller screen
  const isIOS = Platform.OS === "ios";
  const isMobile = windowWidth < 768 || isIOS;

  // State to track which signature is currently being captured
  const [activeSignatureType, setActiveSignatureType] =
    useState<SignatureType>("employee");

  // Handle signature capture based on active type
  const handleSignatureCapture = (fileName: string, imageUrl?: string) => {
    if (activeSignatureType === "employee") {
      handleEmployeeSignature(fileName, imageUrl);
    } else {
      handleManagerSignature(fileName, imageUrl);
    }
  };

  // Handler to reset signature and status for Edit New
  const handleEditNewSignature = () => {
    if (activeSignatureType === "employee") {
      handleEmployeeSignature(null, null);
    } else {
      handleManagerSignature(null, null);
    }
  };

  // Get current signature data based on active type
  const getCurrentSignatureData = () => {
    if (activeSignatureType === "employee") {
      return {
        fileName: employeeSignatureFileName,
        imageUrl: employeeSignatureImageUrl,
        title: "Employee Signature",
      };
    } else {
      return {
        fileName: managerSignatureFileName,
        imageUrl: managerSignatureImageUrl,
        title: "Manager Signature",
      };
    }
  };

  const currentSignature = getCurrentSignatureData();

  return (
    <View
      style={[
        styles.signatureSection,
        isDarkTheme && {
          backgroundColor: colors.background.surface,
          borderWidth: 1,
          borderColor: colors.border.default,
        },
      ]}
    >
      <Text style={styles.sectionHeader}>Signatures Section</Text>

      {/* Signature Type Toggle */}
      <View
        style={[
          styles.toggleContainer,
          isDarkTheme && {
            backgroundColor: colors.background.elevated,
            borderWidth: 1,
            borderColor: colors.border.default,
          },
        ]}
      >
        <TouchableOpacity
          style={[
            styles.toggleButton,
            activeSignatureType === "employee" && styles.activeToggleButton,
            isDarkTheme &&
              activeSignatureType !== "employee" && {
                backgroundColor: colors.background.surface,
                borderWidth: 1,
                borderColor: colors.border.default,
              },
          ]}
          onPress={() => setActiveSignatureType("employee")}
          disabled={false} // Always allow toggle switching for viewing
        >
          <Text
            style={[
              styles.toggleButtonText,
              activeSignatureType === "employee" &&
                styles.activeToggleButtonText,
            ]}
          >
            Employee
          </Text>
          {employeeSignatureFileName && (
            <View style={styles.signatureIndicator} />
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.toggleButton,
            activeSignatureType === "manager" && styles.activeToggleButton,
            isDarkTheme &&
              activeSignatureType !== "manager" && {
                backgroundColor: colors.background.surface,
                borderWidth: 1,
                borderColor: colors.border.default,
              },
          ]}
          onPress={() => setActiveSignatureType("manager")}
          disabled={false} // Always allow toggle switching for viewing
        >
          <Text
            style={[
              styles.toggleButtonText,
              activeSignatureType === "manager" &&
                styles.activeToggleButtonText,
            ]}
          >
            Manager
          </Text>
          {managerSignatureFileName && (
            <View style={styles.signatureIndicator} />
          )}
        </TouchableOpacity>
      </View>

      {/* Single Signature Pad Container */}
      <View style={styles.signaturePadWrapper}>
        <Text style={styles.signatureTitle}>{currentSignature.title}</Text>
        <View
          style={[
            styles.signaturePadContainer,
            isMobile && styles.signaturePadContainerMobile,
            isDarkTheme && {
              backgroundColor: colors.background.elevated,
              borderColor: colors.border.default,
            },
          ]}
        >
          <SignaturePad
            onOK={handleSignatureCapture}
            onClear={handleEditNewSignature}
            initialImageUrl={currentSignature.imageUrl || undefined}
            disabled={isViewOnlyMode}
            containerName="nho"
            isDarkTheme={isDarkTheme}
            surfaceColor={colors.background.surface}
            elevatedColor={colors.background.elevated}
            borderColor={colors.border.default}
            textColor={colors.text.primary}
          />
          {/* Only show uploaded text if signature exists */}
          {currentSignature.fileName && (
            <Text style={styles.signatureText}>
              {currentSignature.title} uploaded
            </Text>
          )}                
          {/* Show disclaimer for employee signature only */}
          {activeSignatureType === "employee" && (
            <SignatureDisclaimer
              disabled={isViewOnlyMode}
            />
          )}
        </View>
      </View>

      {/* Status Summary */}
      <View
        style={[
          styles.statusContainer,
          isDarkTheme && {
            backgroundColor: colors.background.elevated,
            borderWidth: 1,
            borderColor: colors.border.default,
          },
        ]}
      >
        <View style={styles.statusRow}>
          <Text style={styles.statusLabel}>Employee:</Text>
          <Text
            style={[
              styles.statusValue,
              employeeSignatureFileName
                ? styles.statusComplete
                : styles.statusPending,
            ]}
          >
            {employeeSignatureFileName ? "Signed" : "Pending"}
          </Text>
        </View>
        <View style={styles.statusRow}>
          <Text style={styles.statusLabel}>Manager:</Text>
          <Text
            style={[
              styles.statusValue,
              managerSignatureFileName
                ? styles.statusComplete
                : styles.statusPending,
            ]}
          >
            {managerSignatureFileName ? "Signed" : "Pending"}
          </Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  signatureSection: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: Platform.OS === "web" ? 16 : 10,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    width: "100%",
  },
  sectionHeader: {
    fontSize: Platform.OS === "web" ? 17 : 16,
    fontWeight: "bold",
    marginBottom: 16,
    color: "#fff",
    textAlign: "center",
    backgroundColor: "#0066cc", // Blue background
    paddingVertical: 12,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    overflow: "hidden",
  },
  toggleContainer: {
    flexDirection: "row",
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    padding: 4,
    marginBottom: 20,
    gap: 4,
  },
  toggleButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: "transparent",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    position: "relative",
  },
  activeToggleButton: {
    backgroundColor: "#0066cc",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  toggleButtonText: {
    fontSize: Platform.OS === "web" ? 16 : 14,
    fontWeight: "600",
    color: "#2D2D2D",
  },
  activeToggleButtonText: {
    color: "#FFFFFF",
  },
  signatureIndicator: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#28a745",
  },
  signaturePadWrapper: {
    marginBottom: 20,
  },
  signatureTitle: {
    fontSize: Platform.OS === "web" ? 16 : 14,
    fontWeight: "600",
    marginBottom: 10,
    color: "#2D2D2D",
    textAlign: "center",
  },
  signaturePadContainer: {
    borderWidth: 1,
    borderColor: "#ced4da",
    borderRadius: 6,
    padding: 10,
    backgroundColor: "#fff",
    minHeight: Platform.OS === "web" ? 200 : 150,
    maxWidth: Platform.OS === "web" ? "100%" : "100%",
  },
  signaturePadContainerMobile: {
    minHeight: Platform.OS === "ios" ? 140 : 120,
    padding: 6,
    maxWidth: "100%",
    marginTop: Platform.OS === "ios" ? 5 : 0,
  },
  signatureText: {
    marginTop: 8,
    fontSize: Platform.OS === "web" ? 14 : 12,
    color: "#2D2D2D",
    fontWeight: "500",
    textAlign: "center",
  },
  statusContainer: {
    backgroundColor: "white",
    borderRadius: 6,
    padding: 12,
    marginTop: 12,
    width: Platform.OS === "web" ? "12%" : "100%",
  },
  statusRow: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 6,
  },
  statusLabel: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    fontWeight: "500",
    color: "#616161",
    paddingLeft:10,
  },
  statusValue: {
    fontSize: Platform.OS === "web" ? 14 : 12,
    fontWeight: "600",
    paddingLeft:10,
  },
  statusComplete: {
    color: "#129F14",
  },
  statusPending: {
    color: "#616161",
  },
});

export default SignatureSection;
